import React, { useEffect, useState } from "react";
import { Option } from "../../components/Atomos/DropDown/DDMultiple";
import CatalogTableC from "../../components/Catalogs/CatalogTableC";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import ShapeEdit from "../../components/Atomos/Icons/ShapeEdit";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import useDataLoader2 from "../../hooks/useDataLoader2";
import { useCityList } from "../../hooks/Catalogs/useCityList";
import { useStatesList } from "../../hooks/Catalogs/useStates";
import { UseCountryList } from "../../hooks/Catalogs";
import { formatTimeSpanToString } from "../../utils/functions";
import { IAddress } from "../../interfaces/models/IAddress";
import { defaultPageSize } from "../../utils/const";
import Filters, { IFilterData } from "../../components/Catalogs/Filters/Filters";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import { exportAllDataLoader2 } from "../../utils/exportAllDataLoader2";

const Address: React.FC = ({ ...props }) => {
  const { countryData } = UseCountryList();
  const { regionData } = UseRegionList();
  const { cityData } = useCityList();
  const { statesData } = useStatesList();
  const [statesByCountry, setStatesByCountry] = useState<Option[]>();
  const [citiesByState, setCitiesByState] = useState<Option[]>();
  const fields = [
    'id', 
    'address1', 
    'address2', 
    'zipCode', 
    'addressType', 
    'city',
    "cityName",
    "stateName",
    "countryName",
    "regionNames",
    "altitude",
    "latitude",
    "createdDate",
  ]
  const endpoint = '/address';

  const { 
    data, 
    totalCount, 
    fetchData, 
    setFilter, 
    sort, 
    setSort, 
    pagination, 
    setPagination,
    inputFilters,
    setInputFilters,
    filter
  } = useDataLoader2<any>({
    endpoint,
    defaultPageSize,
    fields,
    defaultSorting: { field: "address1", order: "asc" },
  });

  const columnsDef = React.useMemo(() => [
    {
      id: "count",
      header: "#",
      enableSorting: false,
      cell: ({ rowIndex }: { rowIndex: number; row: any; value: any }) => (
        <p> {rowIndex + 1 + pagination.pageIndex * pagination.pageSize} </p>
      ),
    },
    { id: 'address1', header: "Address 1" },
    { id: 'address2', header: "Address 2" },
    { id: 'zipCode', header: "Zip Code" },
    { id: 'addressType', header: "Address Type" },
    { id: 'city', header: "City" },
    { id: 'state', header: "State" },
    { id: 'country', header: "Country" },
    { id: 'regions', header: "Regions" },
    { id: 'createdDate', header: "Created Date" },
    {
      id: 'edit', 
      header: 'Edit', 
      enableSorting: false,
      cell: ({ row, rowIndex }: { rowIndex: number; row: any; value: any }) => (
        <PersistentLink
          className="edit"
          to={`Edit/${row.id}`}
          state={data[rowIndex]}
          persistingData={{
            pagination,
            sort,
            filter, 
            inputFilters, 
          }}
        >
          Edit
          <div className="editsvg"><ShapeEdit /></div>
        </PersistentLink>
      )
    },
  ], [pagination.pageIndex, pagination.pageSize, sort, filter, inputFilters, data]);

  const formatData = (data: IAddress[]) => {
    const formatedData = data.map((d: IAddress) => {
      return {
        ...d,
        city: d.cityName,
        state: d.stateName,
        country: d.countryName,
        regions: d.regionNames,
        createdDate: d.createdDate ? formatTimeSpanToString(d.createdDate) : ''
      }
    })
    return formatedData
  }

  useEffect(() => {
    setStatesByCountry(
      statesData.filter(s => filter?.countries?.value?.includes(s.country))
    )
  }, [filter?.countries]);

  useEffect(() => {
    const cities = cityData?.filter(c => c.state && filter?.states?.value?.includes(c.state))
    setCitiesByState(cities.map(c => ({ id: c.id, name: c.name || '' })))
  }, [filter?.states]);

  const filtersData: IFilterData[] = React.useMemo(() => [
    {
      name: "Name",
      type: "text",
      comparison: "CONTAINS",
    },
    {
      name: "Region",
      fieldName: "regions",
      objectData: regionData?.map(r => ({id: r.id, name: r.name})) || [],
    },
    {
      name: "Country", 
      objectData: countryData?.map(c => ({id: c.id, name: c.name})) || [],
    },
    {
      name: "State",
      fieldName: "states",
      objectData: statesByCountry || [],
      disabled: !statesByCountry?.length
    },
    {
      name: "City",
      fieldName: "city",
      objectData: citiesByState || [],
      disabled: !citiesByState?.length
    },
  ], [regionData, countryData, statesByCountry, citiesByState]);

  return (
    <>
        <CatalogTableC
          title={`Address Catalog`}
          catalogName={"ADDRESSES"}
          catalogShortName={"Address"}
          route={"Address"}
          totalCount={totalCount}
        >
          <Filters
            filtersData={filtersData} 
            filterValues={inputFilters}
            setFilterValues={setInputFilters} 
            Search={() => fetchData()}
            clearSelections={() => fetchData(true)}
            setValueForQuery={setFilter}
          />
          <PaginatedTable
            data={data || []}
            columnsDef={columnsDef}
            formatData={formatData}
            sorting={sort}
            onSortingChange={setSort}
            totalCount={totalCount || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            endpoint={endpoint}
            onExportAll={() =>
              exportAllDataLoader2<any>({
                endpoint,
                filter,
                sort,
                pageSize: 1000,
                fields,
              })
            }
          />
        </CatalogTableC>
    </>
  );
};

export default Address;
